import "./css/index.css";
import React, { forwardRef, memo } from "react";
import classNames from "classnames";
import { ODSButtonProps } from "@/atoms/ODSButton/ODSButton.types";
import ODSRow from "../ODSRow/ODSRow";
import ODSText from "../ODSText/ODSText";
import ODSIcon from "../ODSIcons/ODSIcon";

const ODSButton = forwardRef<HTMLButtonElement, ODSButtonProps>(
  (
    {
      buttonType = "standard",
      variant = "primary",
      size = "large",
      disabled = false,
      leftIcon = false,
      rightIcon = false,
      buttonIcon,
      label,
      ariaLabel,
      className,
      href,
      target,
      rel,
      onClick,
      ...props
    },
    ref
  ) => {
    // overrides the link button styling to standard styling
    const effectiveButtonType = buttonType === "link" ? "standard" : buttonType;

    const ODSButtonClassName = classNames(
      `ODSButton`,
      `ODSButton--size-${size}`,
      `ODSButton--buttonType-${effectiveButtonType}`,
      `ODSButton--variant-${variant}`,
      {
        "ODSButton--disabled": disabled,
      },
      className
    );
    const ODSButtonContentClassName = classNames(
      `ODSButton-Content`,
      `ODSButton-Content--size-${size}`,
      `ODSButton-Content--buttonType-${buttonType}`
    );

    const ODSButtonBgClassName = classNames(
      `ODSButton-ButtonBg--variant-${variant}`,
      `ODSButton-ButtonBg`,
      `ODSButton-ButtonBg--buttonType-${effectiveButtonType}`,
      `ODSButton-ButtonBg--size-${size}`,
      {
        "ODSButton-ButtonBg--disabled": disabled,
      }
    );

    const ODSButtonIconClassName = classNames(
      `ODSButton-ButtonIcon`,
      `ODSButton-ButtonIcon--size-${size}`,
      `ODSButton-ButtonIcon--buttonType-${effectiveButtonType}`,
      `ODSButton-ButtonIcon--variant-${variant}`,
      {
        "ODSButton-ButtonIcon--disabled": disabled,
      }
    );

    const getAriaLabel = () => {
      if (props["aria-label"]) return props["aria-label"];
      if (ariaLabel) return ariaLabel;
      if (label) return label;
      if (buttonIcon) return `button ${buttonIcon.split("-").join(" ")}`;
      return "button";
    };

    const normalizedProps =
      buttonType === "link"
        ? {
            role: "link",
            onClick: () => {
              if (href)
                window.open(
                  href,
                  target ?? "_blank",
                  rel ?? "noopener,noreferrer"
                );
            },
          }
        : { onClick };

    return (
      <button
        ref={ref}
        type="button"
        aria-label={getAriaLabel()}
        className={classNames(ODSButtonClassName, ODSButtonBgClassName)}
        disabled={disabled}
        {...props}
        {...normalizedProps}
      >
        <div className="ODSButton-ButtonBgElement" />

        <ODSRow className={ODSButtonContentClassName}>
          {buttonType === "iconOnly" ? (
            buttonIcon && (
              <ODSIcon
                name={buttonIcon}
                decorative
                className={ODSButtonIconClassName}
              />
            )
          ) : (
            <>
              {leftIcon && (
                <ODSRow
                  className={classNames(
                    `ODSButton-LeftIcon`,
                    `ODSButton-LeftIcon--buttonType-${buttonType}`
                  )}
                >
                  {buttonIcon && (
                    <ODSIcon
                      name={buttonIcon}
                      decorative
                      className={ODSButtonIconClassName}
                    />
                  )}
                </ODSRow>
              )}
              {label && (
                <ODSText as="span" className={`ODSButton-Label`}>
                  {label}
                </ODSText>
              )}
              {rightIcon && (
                <ODSRow
                  className={classNames(
                    `ODSButton-RightIcon`,
                    `ODSButton-RightIcon--buttonType-${buttonType}`
                  )}
                >
                  {buttonIcon && (
                    <ODSIcon
                      name={buttonIcon}
                      decorative
                      className={ODSButtonIconClassName}
                    />
                  )}
                </ODSRow>
              )}
            </>
          )}
        </ODSRow>
      </button>
    );
  }
);

ODSButton.displayName = "ODSButton";

export default memo(ODSButton);
